// AdminPerson ("/people/:id" — also legacy /admin/staff/:id and /admin/people/:id).
// Public to all authenticated users. Shows the staff member's full RACI
// involvement (R + A + C + I) so personnel review reflects real workload,
// not just primary-responsibility headcount.

import { useState } from 'react';
import { parseHash, navigate } from '../lib/routing.js';
import { Icon } from '../components/Icon.jsx';
import { Avatar } from '../components/Avatar.jsx';
import { ListView } from '../components/ListView.jsx';

const ROLE_LABEL = { R: '负责', A: '审批', C: '咨询', I: '通知' };

export const AdminPerson = ({ user, tasks, staff = [], onCreate }) => {
  const { parts } = parseHash();
  // Always take the last path segment — robust to /people/:id,
  // /admin/staff/:id, and /admin/people/:id. (The bug was parts[2].)
  const staffId = parts[parts.length - 1];
  const [filter, setFilter] = useState('全部');
  const [roleScope, setRoleScope] = useState('all');   // 'all' | 'R' (just primary)
  const person = staff.find(s => s.id === staffId);

  // Pull every task where the person has ANY RACI role. Role map by task id.
  const myTasksWithRole = tasks.flatMap(t => {
    const myRoles = (t.owners || [])
      .filter(o => o.staffId === staffId)
      .map(o => o.role);
    if (myRoles.length === 0) return [];
    return [{ task: t, roles: myRoles }];
  });
  const myTasks = roleScope === 'R'
    ? myTasksWithRole.filter(x => x.roles.includes('R')).map(x => x.task)
    : myTasksWithRole.map(x => x.task);
  const counts = {
    R: myTasksWithRole.filter(x => x.roles.includes('R')).length,
    A: myTasksWithRole.filter(x => x.roles.includes('A')).length,
    C: myTasksWithRole.filter(x => x.roles.includes('C')).length,
    I: myTasksWithRole.filter(x => x.roles.includes('I')).length
  };

  if (!person) {
    return (
      <>
        <button className="detail-back" onClick={() => navigate('/admin')}><Icon name="back"/>返回总览</button>
        <div className="vava-card empty-state">
          <div className="title">员工不存在</div>
          <div className="sub">ID: <span className="mono">{staffId || '(missing)'}</span></div>
        </div>
      </>
    );
  }

  const totalShown = myTasks.length;

  return (
    <>
      <button className="detail-back" onClick={() => navigate('/admin')}><Icon name="back"/>返回总览</button>
      <div className="vava-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar staff={person} large />
          <div>
            <h1 className="vava-page-title" style={{ margin: 0 }}>{person.name}</h1>
            <div className="vava-page-meta">{person.title}{person.dept ? ' · ' + person.dept : ''}</div>
          </div>
        </div>
        {onCreate && (
          <button className="btn btn-primary" onClick={() => onCreate({ assigneeId: person.id })}>
            <Icon name="plus"/> 给 {person.name} 派待办
          </button>
        )}
      </div>

      <div className="raci-summary-row">
        <button className={`raci-summary-tile ${roleScope === 'R' ? 'active' : ''}`}
                onClick={() => setRoleScope(roleScope === 'R' ? 'all' : 'R')}
                title="点击只看主负责待办">
          <span className="raci-summary-role">R · 负责</span>
          <span className="raci-summary-num mono">{counts.R}</span>
        </button>
        <div className="raci-summary-tile static">
          <span className="raci-summary-role">A · 审批</span>
          <span className="raci-summary-num mono">{counts.A}</span>
        </div>
        <div className="raci-summary-tile static">
          <span className="raci-summary-role">C · 咨询</span>
          <span className="raci-summary-num mono">{counts.C}</span>
        </div>
        <div className="raci-summary-tile static">
          <span className="raci-summary-role">I · 通知</span>
          <span className="raci-summary-num mono">{counts.I}</span>
        </div>
        <div className="raci-summary-total">
          {roleScope === 'R' ? '只看 R · ' : ''}显示 {totalShown} 个待办
          {roleScope === 'R' && (
            <button onClick={() => setRoleScope('all')} className="raci-summary-reset">显示全部 RACI</button>
          )}
        </div>
      </div>

      <ListView tasks={myTasks} filter={filter} setFilter={setFilter}
                openTask={(id) => navigate('/task/' + id)} showAssignee={false} staff={staff} />
    </>
  );
};
